import { Grid, Progress, Tooltip } from '@mantine/core';
import { type PropsWithChildren, type ReactNode, useState } from 'react';

import { type CountSummary, CountType } from '@dte/otw-azure-functions/src/common/interfaces/containers/config/Count';
import { percentage } from '@dte/otw-utils-core/src/numbers/percentage';
import { roundedString } from '@dte/otw-utils-core/src/numbers/roundedString';

import { AuthenticatedJsonContent } from '../../../app/(client)/(authenticated)/content/jsonGet/AuthenticatedJsonContent';
import { formatCount } from '../formatCount';
import { getCountSummaryURL } from './getCountSummaryURL';
import styles from './OutageCountBar.module.scss';

interface CountSummaryProps {
	countSummary: CountSummary | undefined;
}

function roundPercentageWithoutPower(value: number, count: number): string {
	let rounded = roundedString(value, 2);

	// If 100% but we still have outages, show as 99.99%
	if (count > 0) {
		if (rounded === '100') {
			rounded = '99.99';
		}

		if (rounded === '0') {
			rounded = '0.01';
		}
	}

	return rounded;
}

function getOutageCount(countSummary: CountSummary | undefined): number {
	const counts = countSummary?.counts ?? {};
	return counts[CountType.DisplayOutages] ?? 0;
}

function getPremiseCount(countSummary: CountSummary | undefined): number {
	const counts = countSummary?.counts ?? {};
	return counts[CountType.Premises] ?? 2000000;
}

function getPercentOutOfPower(countSummary: CountSummary | undefined): number {
	const outageCount = getOutageCount(countSummary);
	const premiseCount = getPremiseCount(countSummary);
	return percentage(outageCount, premiseCount);
}

function OutageCountBar({ countSummary }: CountSummaryProps): ReactNode {
	const percentOut = getPercentOutOfPower(countSummary);

	return (
		<section className={styles['main']}>
			<Progress value={percentOut} className={styles['outages']} />
		</section>
	);
}

function OutagePercentTooltip({ countSummary, children }: CountSummaryProps & PropsWithChildren): ReactNode {
	let message: ReactNode = <>Loading...</>;
	if (countSummary) {
		const outageCount = getOutageCount(countSummary);
		const percentOut = getPercentOutOfPower(countSummary);

		if (outageCount > 0) {
			const formatPercent = roundPercentageWithoutPower(percentOut, outageCount);
			message = (
				<>
					<b>{formatPercent}%</b> of customers out
				</>
			);
		}
		// If negative, treat as no customers out
		else {
			message = <>No customers out</>;
		}
	}

	return (
		<Tooltip withArrow={true} arrowSize={8} arrowOffset={16} position={'bottom-start'} label={message}>
			<div>{children}</div>
		</Tooltip>
	);
}

function BriefCountDetails({ countSummary }: CountSummaryProps): ReactNode {
	const outageCount = getOutageCount(countSummary);
	if (outageCount === undefined) {
		return <>&nbsp;</>;
	}
	return <>{formatCount(outageCount)}</>;
}

function OutageCountSummaryContent({ countSummary }: CountSummaryProps): ReactNode {
	return (
		<OutagePercentTooltip countSummary={countSummary}>
			<Grid gap={0}>
				<Grid.Col span={5}>
					<b>Customers Out</b>
				</Grid.Col>
				<Grid.Col span={7}>
					<BriefCountDetails countSummary={countSummary} />
				</Grid.Col>
			</Grid>
			<OutageCountBar countSummary={countSummary} />
		</OutagePercentTooltip>
	);
}

export function OutageCountSummary(): ReactNode {
	const [countSummary, setCountSummary] = useState<CountSummary>();

	const url = getCountSummaryURL();

	return (
		<>
			<OutageCountSummaryContent countSummary={countSummary} />
			<AuthenticatedJsonContent url={url} setData={setCountSummary} />
		</>
	);
}
